import { Fragment } from "react";
import { Card, CardContent } from "@components/ui/card";
import { Chat, useTurns, useAgentActivity, extractText } from "@launch10/use-langgraph-stream";
import { ThinkingIndicator } from "@components/shared/chat/ThinkingIndicator";
import { ChatErrorBanner } from "@components/shared/chat/ChatErrorBanner";
import { CreditGate } from "@components/shared/chat/input/CreditGate";
import { ArrowUpIcon, StopIcon, DocumentPlusIcon } from "@heroicons/react/24/outline";
import { useSupportMessages, useSupportIsStreaming } from "@hooks/useSupportChat";

export default function SupportChat() {
  const messages = useSupportMessages();
  const isStreaming = useSupportIsStreaming();
  const turns = useTurns(messages);
  const { byMessage, isLive } = useAgentActivity({ messages, isStreaming });

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex flex-col h-[500px] bg-white">
          <Chat.Messages.List className="flex-1 overflow-y-auto p-4">
            {turns.map((turn) => {
              const lastIndex = turn.messages.length - 1;
              let aiHeaderShown = false;
              return (
                <div key={turn.id} className="space-y-3">
                  {turn.messages.map((msg, i) => {
                    if (msg.type === "tool") return;
                    if (msg.type === "human") {
                      return <Chat.UserMessage key={msg.id} message={msg} />;
                    }
                    if (msg.type !== "ai") return;
                    const isLastInTurn = turn.isLatest && i === lastIndex;
                    const activity = msg.id ? byMessage.get(msg.id) : undefined;
                    const continuation = aiHeaderShown;
                    if (extractText(msg.content)?.trim()) aiHeaderShown = true;
                    return (
                      <Fragment key={msg.id}>
                        <Chat.AIMessage
                          message={msg}
                          isStreaming={isLastInTurn && isStreaming}
                          continuation={continuation}
                          className="text-sm"
                        />
                        {activity && (
                          <Chat.ToolActivity
                            group={activity}
                            isStreaming={turn.isLatest && isStreaming}
                          />
                        )}
                      </Fragment>
                    );
                  })}
                </div>
              );
            })}
            <Chat.QueuedMessages />
            {isStreaming && !isLive && <ThinkingIndicator text="Thinking" />}
            <ChatErrorBanner />
          </Chat.Messages.List>

          <div className="border-t border-neutral-200 p-3">
            <CreditGate>
              <Chat.Input.DropZone className="relative bg-white border border-neutral-300 rounded-xl p-4 flex flex-col min-h-[100px]">
                <Chat.Input.AttachmentList className="flex flex-wrap gap-2 mb-3" />
                <Chat.Input.Textarea
                  placeholder="Ask a question..."
                  className="flex-1 text-base-500 placeholder:text-neutral-500 placeholder:opacity-100"
                />
                <div className="flex items-center justify-between mt-auto pt-2">
                  <Chat.Input.FileButton className="text-base-500 p-1 hover:bg-neutral-100 rounded">
                    <DocumentPlusIcon className="w-5 h-5" strokeWidth={1.5} />
                  </Chat.Input.FileButton>
                  <Chat.Input.SubmitButton
                    stopIcon={<StopIcon className="w-4 h-4" strokeWidth={2} />}
                    className="w-6 h-6 rounded-full bg-secondary-500 text-white hover:bg-secondary-600"
                  >
                    <ArrowUpIcon className="w-4 h-4" strokeWidth={2} />
                  </Chat.Input.SubmitButton>
                </div>
              </Chat.Input.DropZone>
            </CreditGate>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
